import {createStore} from 'vuex'
import BaudarlehenInitialisierungsTO from "@/types/BaudarlehenInitialisierungsTO";
import SliderTO from "@/types/SliderTO";
import SollzinsbindungTO from "@/types/SollzinsbindungTO";
import BaudarlehenErgebnisTO from "@/types/BaudarlehenErgebnisTO";
import BaudarlehenVergleichsergebnisTO from "@/types/BaudarlehenVergleichsergebnisTO";
import {trackProduct} from "@/utils/TrackingUtil";

export default createStore({
    state: {
        //Sliderwerte müssen als Default gesetzt werden, da sonst noUiSlider Fehler wirft
        sliderPreis: {max: 750000, min: 50000, step: 10000, value: 100000} as SliderTO,
        sliderDarlehen: {max: 750000, min: 50000, step: 10000, value: 80000} as SliderTO,
        sliderRate: {max: 5000, min: 200, step: 10, value: 750} as SliderTO,
        sliderSondertilgung: {max: 3000, min: 0, step: 100, value: 0} as SliderTO,
        sollzinsbindungen: [] as Array<SollzinsbindungTO>,
        dauerSollzinsbindungDefaultValue: Number,
        selectedSollzinsbindung: Number,
        sondertilgungVisible: Boolean,
        volltilgungsoptionVisible: Boolean,
        volltilgungsoptionChecked: Boolean,
        fachtexte: Object,
        berechnungsergebnis: {} as BaudarlehenErgebnisTO,
        isCalculating: Boolean,
        isCalculatingVergleich: Boolean,
        vergleichsmodelle: {} as BaudarlehenVergleichsergebnisTO,
        initDataLoaded: false,
        rechnerNameGAEvents: String,

        //Zusatzwerte für die Finanzierungsbestätigung
        sliderAusgaben: {max: 10000, min: 0, step: 100, value: 800} as SliderTO,
        sliderEinnahmen: {max: 10000, min: 0, step: 100, value: 1000} as SliderTO,
        sliderEigenkapital: {max: 750000, min: 20000, step: 10000, value: 40000} as SliderTO,
    },
    mutations: {
        initDefaultValues(state: any, defaultValues: BaudarlehenInitialisierungsTO) {
            state.sliderPreis = defaultValues.sliderPreis;
            state.sliderDarlehen = defaultValues.sliderDarlehen;
            state.sliderRate = defaultValues.sliderRate;
            state.sollzinsbindungen = defaultValues.sollzinsbindungTO;
            state.dauerSollzinsbindungDefaultValue = defaultValues.dauerSollzinsbindungDefaultValue;
            state.selectedSollzinsbindung = defaultValues.dauerSollzinsbindungDefaultValue;
            state.sondertilgungVisible = defaultValues.sondertilgungVisible;
            state.volltilgungsoptionVisible = defaultValues.volltilgungsoptionVisible;
            state.volltilgungsoptionChecked = defaultValues.volltilgungsoptionChecked;
            state.fachtexte = defaultValues.fachtexte;
            state.berechnungsergebnis = defaultValues.berechnungsergebnis;
            state.initDataLoaded = true;

            //Zusatzwerte für die Finanzierungsbestätigung
            state.sliderAusgaben = defaultValues.sliderAusgaben;
            state.sliderEinnahmen = defaultValues.sliderEinnahmen;
            state.sliderEigenkapital = defaultValues.sliderEigenkapital;
        },
        handleResult(state: any, result: BaudarlehenErgebnisTO) {
            //Werte für Rate werden separat gesetzt, damit der Hinweis nur gesetzt wird wenn einer mitkommt
            state.sliderRate.value = result.rate.value;
            state.sliderRate.min = result.rate.min;
            state.sliderRate.max = result.rate.max;
            state.sliderRate.step = result.rate.step;
            // 1. Fall: Hinweis steht in der Backend Response, dann immer setzen
            if (result.rate.hinweis != null) {
                state.sliderRate.hinweis = result.rate.hinweis;
            } else {
                // 2. Fall: Hinweis ist im State, aber nicht in der Backend Response
                // In diesem Fall Hinweis nur zurücksetzen wenn sich die Soll-Zins Bindung geändert hat (User hat das Tab gewechselt)
                // Hintergrund: bei manueller Eingabe ins Textfeld wird Client-seitig der Wert validiert und ein Hinweis gesetzt
                // falls der Wert ungültig ist. Ein Backend Request wird dann mit korrigiertem Wert geschickt.
                // Der Hinweis soll aber verschwinden wenn das Tab (Sollzinsbindung) gewechselt wird.
                const { jahre: jahreState, monate: monateState } = state.berechnungsergebnis.berechnungsdetails.sollzinsbindung;
                const { jahre: jahreResult, monate: monateResult } = result.berechnungsdetails.sollzinsbindung;
                const hasSollzinsbindungChanged = jahreState !== jahreResult || monateState !== monateResult
                if (state.sliderRate.hinweis && hasSollzinsbindungChanged) {
                    state.sliderRate.hinweis = null
                }
            }

            state.sliderDarlehen = result.sliderDarlehen;
            state.sliderSondertilgung = result.sliderSondertilgung;
            state.isCalculating = false;
            state.berechnungsergebnis = result;

            //Timeout, damit es sicher erst nach den DOM Ready vom Google Tagmanager getrackt wird
            setTimeout(() => {
                trackProduct(result.berechnungsdetails, state.sliderPreis.value);
            }, 1000);
        },
        handleVergleichsmodelle(state: any, result: BaudarlehenVergleichsergebnisTO) {
            state.vergleichsmodelle = result;
            state.isCalculatingVergleich = false;
        },
        updateKaufpreis(state: any, kaufpreis: number) {
            state.sliderPreis.value = kaufpreis;
            state.isCalculating = true;
            state.isCalculatingVergleich = true;
        },
        updateDarlehen(state: any, darlehen: number) { //löst eine Berechnung aus
            state.sliderDarlehen.value = darlehen;
            state.isCalculating = true;
            state.isCalculatingVergleich = true;
        },
        //löst keine Berechnung aus, wird aufgerufen um das Darlehen zu aktualisieren, wenn der Kaufpreis unter das Darlehen fällt.
        //Die Berechnung wird in dem Fall von der updateKaufpreis ausgelöst und es soll keine zweite geben
        updateDarlehenAfterKaufpreis(state: any, darlehen: number) {
            state.sliderDarlehen.value = darlehen;
        },
        updateRate(state: any, rate: number) {
            state.sliderRate.value = rate;
            state.isCalculating = true;
            state.isCalculatingVergleich = true;
        },
        updateSondertilgung(state: any, sondertilgung: number) {
            state.sliderSondertilgung.value = sondertilgung;
            state.isCalculating = true;
            state.isCalculatingVergleich = true;
        },
        updateSollzinsbindung(state: any, sollzinsbindung: number) {
            state.selectedSollzinsbindung = sollzinsbindung;
            state.isCalculating = true;
            state.isCalculatingVergleich = true;
        },
        updateVolltilgung(state: any, volltilgung: boolean) {
            state.volltilgungsoptionChecked = volltilgung;
            state.isCalculating = true;
            state.isCalculatingVergleich = true;
        },

        //Zusätzliche Mutationen für die Finanzierungsbesätigung
        updateEinnahmen(state: any, einnahmen: number) {
            let rate = einnahmen - state.sliderAusgaben.value;
            if (rate < 0) {
                rate = 0;
            }
            state.sliderEinnahmen.value = einnahmen;
            state.sliderRate.value = rate;
            state.isCalculating = true;
            state.isCalculatingVergleich = true;
        },
        updateAusgaben(state: any, ausgaben: number) {
            state.sliderAusgaben.value = ausgaben;
            state.isCalculating = true;
            state.isCalculatingVergleich = true;
        },
        updateEigenkapital(state: any, eigenkapital: number) {
            state.sliderEigenkapital.value = eigenkapital;
            state.isCalculating = true;
            state.isCalculatingVergleich = true;
        },
    }
})
